/**
 * DriverOnboardingChecklist — guided first-login checklist (P3).
 * Read-only view over GET /driver/onboarding-checklist. Shows a progress
 * ring + per-item status so a new driver knows exactly what's outstanding
 * (profile, vehicle, documents, bank). No dispatch / lifecycle logic touched.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";

export const DriverOnboardingChecklist = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchChecklist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/driver/onboarding-checklist");
      setData(res.data || null);
    } catch (e) {
      // Non-fatal — the documents/payment panels still work without it.
      console.warn(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  if (loading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="onboarding-checklist-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading your checklist…
      </div>
    );
  }
  if (!data) return null;

  const { score, items, complete } = data;

  return (
    <div className="border border-border bg-card p-4" data-testid="driver-onboarding-checklist">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading text-lg font-black tracking-tight">Getting started</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {complete
              ? "All steps complete — an operator will review and approve your account."
              : "Finish these steps to get approved and start driving."}
          </p>
        </div>
        <div className="text-right">
          <div
            className="font-heading text-3xl font-black tabular-nums"
            style={{ color: complete ? "#10B981" : "#0A0A0A" }}
            data-testid="onboarding-score"
          >
            {score}%
          </div>
          <div className="label-mini text-muted-foreground">COMPLETE</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden mb-3">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${score}%`, backgroundColor: complete ? "#10B981" : "#FFC107" }}
        />
      </div>

      <ul className="space-y-1.5">
        {items.map((it) => (
          <li
            key={it.key}
            className="flex items-center gap-2 text-sm"
            data-testid={`checklist-item-${it.key}`}
          >
            {it.done ? (
              <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0" />
            ) : (
              <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
            <span className={it.done ? "text-muted-foreground line-through" : "font-medium"}>
              {it.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DriverOnboardingChecklist;
